import axios from 'axios';
import { storageGet, storageSet, storageRemove } from '../Collector/StorageAction';
import { cookieGet } from '../Collector/CookieAction';
import { API_BASE } from '../Api/Config'
import { isGuestSurface } from '../Auth/guestSurface'
import { applyClientLanguage } from '../i18n/localeId'

/**
 * Product / MakeBooking / Reservation modülleri için axios örneği.
 * baseURL = yeni backend (REACT_APP_ADDRESS). Çağrılar `Checkout/method` biçiminde.
 * JWT Bearer token otomatik eklenir; 401'de refresh-token ile sessiz yenileme + retry
 * (AxiosConfig ile aynı mantık) — token süresi dolunca çağrılar overlay'e düşmesin diye.
 */
const api = axios.create({
    baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
    // Guest yüzeyinde (widget iframe'i / storefront) panel token'ı gönderilmez → Share/Widget
    // /Storefront başlıklarıyla ziyaretçi olarak çalışır, panelin oturumuna dokunmaz.
    const token = isGuestSurface() ? '' : storageGet('token');
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    } else {
        // Token yoksa (guest): share VEYA widget oturumu varsa kimliği gönder.
        // Backend Authenticate bunu partner/paylaşan adına (whitelist'li uçlarda) kabul eder.
        try {
            const sc = sessionStorage.getItem('tz_share_code');
            if (sc) config.headers['Share'] = sc;
            const wk = sessionStorage.getItem('tz_widget_key');
            if (wk) {
                config.headers['Widget'] = wk;
                const wo = sessionStorage.getItem('tz_widget_origin');
                if (wo) config.headers['Widget-Origin'] = wo;
            }
            const sf = sessionStorage.getItem('tz_storefront');
            if (sf) config.headers['Storefront'] = sf;
        } catch (e) { /* SSR/private mode */ }
    }
    // Katalog içerik dili = canlı UI dili (body.language verilmişse o kazanır).
    return applyClientLanguage(config);
});

// --- 401 refresh-token kuyruğu (eşzamanlı isteklerde tek yenileme) ---
let isRefreshing = false;
let failedQueue = [];
const processQueue = (error, token = null) => {
    failedQueue.forEach((p) => (token ? p.resolve(token) : p.reject(error)));
    failedQueue = [];
};
const forceLogout = () => {
    // Gömme yüzeyi (widget iframe'i / storefront) panelin token'ını SİLMEZ — aynı localStorage.
    if (isGuestSurface()) return;
    storageRemove('token');
    storageRemove('refreshToken');
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/Login')) {
        window.location.href = '/';
    }
};

api.interceptors.response.use(
    (res) => {
        // Yeni framework zarfı { status, payload, timestamp } -> res.data = payload
        if (res && res.data && typeof res.data === 'object' && res.data.payload !== undefined) {
            res.data = res.data.payload;
        }
        return res;
    },
    async (error) => {
        const originalRequest = error.config;
        if (typeof error.response === 'undefined' || !originalRequest) {
            return Promise.reject(error);
        }
        // Guest yüzeyinde yenileme YOK — paylaşılan refresh token'ı tüketmemeli.
        const refreshToken = isGuestSurface() ? '' : storageGet('refreshToken');

        if (error.response.status === 401 && refreshToken && !originalRequest._retry) {
            originalRequest._retry = true;

            if (isRefreshing) {
                // başka bir yenileme sürüyor → kuyruğa gir, token gelince retry et
                return new Promise((resolve, reject) => failedQueue.push({ resolve, reject }))
                    .then((token) => {
                        originalRequest.headers['Authorization'] = `Bearer ${token}`;
                        return api(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            isRefreshing = true;
            try {
                const res = await axios.post(`${API_BASE}/Login/RefreshToken`, {
                    clientId: storageGet('clientId'),
                    activityId: storageGet('activityId'),
                    csrf: cookieGet('csrf'),
                    refreshToken,
                });
                const payload = res.data && res.data.payload;
                if (res.status === 200 && payload && payload.status) {
                    const newToken = payload.data.token;
                    storageSet('token', newToken);
                    processQueue(null, newToken);
                    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                    return api(originalRequest);
                }
                processQueue(new Error('refresh_failed'), null);
                forceLogout();
            } catch (err) {
                processQueue(err, null);
                forceLogout();
            } finally {
                isRefreshing = false;
            }
        } else if (error.response.status === 401 && !refreshToken) {
            // refresh token yok → oturum bitti
            forceLogout();
        }

        return Promise.reject(error);
    }
);

export default api;
